import {
  Button,
  TableRow,
  TableCellProps,
  TableCell,
  MenuItem,
  Typography,
  Box,
} from "@mui/material";
import { CSSProperties } from "@mui/material/styles/createMixins";
import { Formik, FormikHelpers } from "formik";
import { ReactElement } from "react";
import * as Yup from "yup";

import RouteLeavingGuard from "~frontend/components/RouteLeavingGuard";
import TooltipButton from "~frontend/components/TooltipButton";
import { FormikSelect } from "~frontend/components/forms/FormSelect";
import { FormikTextField } from "~frontend/components/forms/FormTextField";
import theme from "~frontend/theme";
import { contactFieldNameSchema } from "~frontend/util/contactFields";

import { CampaignContactField, CampaignForEditor } from "../types";

type EditFormValues = { contactDataKey: string; name: string };

export function ControlsCell(props: TableCellProps): ReactElement {
  return (
    <TableCell
      key="controls"
      align="right"
      sx={{
        height: "38px",
      }}
      {...props}
    />
  );
}

export function NameCell(props: TableCellProps): ReactElement {
  return (
    <TableCell key="name" sx={{ fontWeight: 400, flex: "1" }} {...props} />
  );
}

export function MappingCell(props: TableCellProps): ReactElement {
  return (
    <TableCell key="mapping" sx={{ fontWeight: 400, flex: "1" }} {...props} />
  );
}

const CONTACT_ROW_BUTTON_HEIGHT = 27;
const DELETE_BUTTON_WIDTH = 35;
const INPUT_FONT_SIZE = 12;
const NOT_MAPPED = "Not mapped";

export default function ContactFieldTableRow({
  isNew = false,
  contactField,
  campaign,
  onSubmit,
  onDelete,
  contactDataKeys,
  required,
  dataSourceFieldLabel,
  editing,
  editDisabled,
  onToggleEditing,
}: {
  isNew?: boolean;
  contactField?: CampaignContactField;
  campaign: CampaignForEditor;
  onSubmit: (args: {
    contactDataKey: string | null;
    name: string;
  }) => Promise<void>;
  onDelete?: () => Promise<void>;
  contactDataKeys: string[];
  required: false | { reason: string };
  dataSourceFieldLabel: string;
  editing: boolean;
  editDisabled: boolean;
  onToggleEditing: (e: boolean) => void;
}): ReactElement {
  if (!isNew && !contactField) {
    throw new Error("Contact field must be provided if not new");
  }

  const builtIn = !isNew && contactField?.builtIn;

  const handleSubmitEdits = async (
    { contactDataKey, name }: EditFormValues,
    { setSubmitting }: FormikHelpers<EditFormValues>
  ) => {
    await onSubmit({
      contactDataKey:
        contactDataKey.length > 0 && contactDataKey !== NOT_MAPPED
          ? contactDataKey
          : null,
      name,
    });
    setSubmitting(false);
    onToggleEditing(false);
  };

  function renderActionButton(
    action: "Edit" | "Delete",
    onClick: (() => Promise<void> | void) | undefined,
    sx: CSSProperties = {}
  ) {
    if (!contactField) {
      return null;
    }

    let disabledReason: string | null = null;

    if (contactField.builtIn && action === "Delete") {
      disabledReason = "Built-in fields cannot be deleted";
    } else if (required && action === "Delete") {
      disabledReason = "Cannot delete fields used in scripts";
    } else if (editDisabled && action === "Edit") {
      disabledReason = "Another field is being edited";
    }

    const style = { height: CONTACT_ROW_BUTTON_HEIGHT, ...sx };

    return (
      <TooltipButton
        variant="contained"
        color="accessory"
        onClick={onClick}
        sx={style}
        disabled={!!disabledReason}
        tooltipText={disabledReason}
      >
        {action}
      </TooltipButton>
    );
  }

  return editing ? (
    <Formik
      initialValues={{
        contactDataKey: contactField?.contactDataKey || NOT_MAPPED,
        name: contactField?.name || "",
      }}
      validationSchema={Yup.object({
        contactDataKey: campaign.startedAt
          ? Yup.string().required("Required").notOneOf([NOT_MAPPED], "Required")
          : Yup.string(),
        name: contactFieldNameSchema({
          otherFieldNames: campaign.contactFields
            .map(field => field.name)
            .filter(name => name !== contactField?.name),
        }),
      })}
      onSubmit={handleSubmitEdits}
    >
      {({ dirty, values, handleSubmit, handleReset }) => (
        <>
          <RouteLeavingGuard when={dirty} />
          <TableRow>
            <MappingCell
              sx={{
                paddingTop: 0,
              }}
            >
              <Box sx={{ minHeight: "66px" }}>
                <FormikSelect
                  fullWidth
                  variant="underlined"
                  name="contactDataKey"
                  sx={{ fontSize: INPUT_FONT_SIZE }}
                  label={dataSourceFieldLabel}
                  renderValue={value => (
                    <>
                      {(value as string).length
                        ? (value as string)
                        : NOT_MAPPED}
                    </>
                  )}
                >
                  <MenuItem value={NOT_MAPPED}>
                    <em>Not mapped to a {dataSourceFieldLabel}</em>
                  </MenuItem>
                  {contactDataKeys.map(contactDataKey => (
                    <MenuItem key={contactDataKey} value={contactDataKey}>
                      {contactDataKey}
                    </MenuItem>
                  ))}
                </FormikSelect>
              </Box>
            </MappingCell>
            <NameCell
              sx={{
                paddingTop: 0,
              }}
            >
              <Box sx={{ minHeight: "66px" }}>
                {!isNew && (builtIn || required) ? (
                  <Box sx={{ marginTop: "11px" }}>{values.name}</Box>
                ) : (
                  <FormikTextField
                    variant="standard"
                    name="name"
                    fullWidth
                    label={`${isNew ? "New " : ""}Contact Field Name`}
                    InputProps={{ sx: { fontSize: INPUT_FONT_SIZE } }}
                  />
                )}
              </Box>
            </NameCell>
            <ControlsCell>
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "flex-end",
                  gap: theme.spacing(1),
                }}
              >
                <Button
                  variant="contained"
                  color="primary"
                  type="submit"
                  sx={{
                    height: CONTACT_ROW_BUTTON_HEIGHT,
                    width: 27,
                    paddingLeft: theme.spacing(0.75),
                    paddingRight: theme.spacing(0.75),
                  }}
                  onClick={() => handleSubmit()}
                >
                  Save
                </Button>
                <Button
                  variant="contained"
                  color="accessory"
                  sx={{
                    height: CONTACT_ROW_BUTTON_HEIGHT,
                    paddingLeft: theme.spacing(0.5),
                    paddingRight: theme.spacing(0.5),
                    width: 42,
                  }}
                  onClick={() => {
                    handleReset();
                    onToggleEditing(false);
                  }}
                >
                  Cancel
                </Button>
              </Box>
            </ControlsCell>
          </TableRow>
        </>
      )}
    </Formik>
  ) : (
    <TableRow>
      <MappingCell>
        {contactField?.contactDataKey || (
          <>
            <Typography sx={{ fontStyle: "italic" }} variant="body2">
              {NOT_MAPPED}
            </Typography>
            {required ? (
              <Typography
                variant="body2"
                sx={{
                  display: "block",
                }}
              >
                Required - {required.reason}
              </Typography>
            ) : null}
          </>
        )}
      </MappingCell>
      <NameCell>{contactField?.name}</NameCell>
      <ControlsCell>
        <div
          style={{
            display: "flex",
            justifyContent: "flex-end",
            gap: theme.spacing(1),
          }}
        >
          {renderActionButton("Edit", () => onToggleEditing(true), {
            width: 22,
          })}
          {renderActionButton("Delete", onDelete, {
            width: DELETE_BUTTON_WIDTH,
          })}
        </div>
      </ControlsCell>
    </TableRow>
  );
}
